/**
 * React bindings for the Clarity layer.
 *
 *  - <ClarityRouteObserver/> : mount INSIDE the Router. Tags each route, drives journey
 *    abandonment, re-applies masking on lazy routes, and wires one-time perf observers.
 *  - <ClaritySession/>       : mount anywhere with store access. Emits safe user/session/
 *    tenant/role tags (and optional opaque identify) whenever auth state changes.
 *
 * Both render null. Both are inert when Clarity is disabled (the underlying calls no-op).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { usePermissionStore } from '../../store/usePermissionStore';
import { identifyClarityUser, maskSensitiveClarityRegions, setClarityTags } from './clarity';
import { roleLabel } from './events';
import { journeyTracker } from './journeys';
import { trackPerformanceSignal, trackRouteView } from './signals';

function authStateOf(isAuthenticated: boolean, isImpersonating: boolean): RouteAuthState {
  if (!isAuthenticated) return 'anonymous';
  return isImpersonating ? 'impersonating' : 'authenticated';
}
type RouteAuthState = 'anonymous' | 'authenticated' | 'impersonating';

export function ClarityRouteObserver(): null {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);

  // One-time performance observers (initial load + long tasks).
  useEffect(() => {
    try {
      const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
      if (nav?.duration) {
        trackPerformanceSignal({ kind: 'route', durationMs: nav.duration, label: 'initial-load', slowMs: 4000 });
      }
      if ('PerformanceObserver' in window) {
        const obs = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            // Only long tasks worth surfacing (>50ms is the spec's long-task threshold).
            trackPerformanceSignal({ kind: 'long-task', durationMs: entry.duration, slowMs: 50 });
          }
        });
        obs.observe({ entryTypes: ['longtask'] });
        return () => obs.disconnect();
      }
    } catch {
      /* perf observability is best-effort */
    }
    return undefined;
  }, []);

  useEffect(() => {
    trackRouteView({
      pathname: location.pathname,
      authState: authStateOf(isAuthenticated, isImpersonating),
    });
    journeyTracker.onRouteChange(location.pathname);
    // Re-apply masking after the route's (possibly lazy) content has mounted.
    maskSensitiveClarityRegions();
  }, [location.pathname, isAuthenticated, isImpersonating]);

  return null;
}

interface BillingShape {
  subscriptionStatus?: string | null;
  subscriptionPlanName?: string | null;
}

export function ClaritySession(): null {
  const user = useAuthStore((s) => s.user);
  const business = useAuthStore((s) => s.business);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const permissionRole = usePermissionStore((s) => s.role);

  const lastIdentified = useRef<string | null>(null);

  useEffect(() => {
    const role = roleLabel(user?.role);
    const isManagement = user?.role !== undefined && [0, 1, 5, 6].includes(user.role);
    // Subscription fields ride on the bootstrap business object (not in the persisted type).
    const billing = (business ?? {}) as BillingShape;

    setClarityTags({
      auth_state: authStateOf(isAuthenticated, isImpersonating),
      user_role: role,
      user_persona: isAuthenticated ? (isManagement ? 'management' : 'staff') : undefined,
      permission_role: permissionRole || undefined,
      tenant_type: business?.businessType,
      tenant_status: isImpersonating ? 'impersonated' : isAuthenticated ? 'active' : undefined,
      account_status: isImpersonating ? 'impersonated' : undefined,
      onboarding_state: business?.onboardingStatus,
      plan: billing.subscriptionPlanName ?? undefined,
      billing_state: billing.subscriptionStatus ?? undefined,
      password_state: user ? (user.isFirstLogin ? 'must_change' : 'ok') : undefined,
    });

    // Opaque identify (off unless VITE_CLARITY_IDENTIFY=true). Never an email.
    if (isAuthenticated && user?.id && lastIdentified.current !== user.id) {
      identifyClarityUser({ customId: user.id, friendlyName: role });
      lastIdentified.current = user.id;
    }
    if (!isAuthenticated) lastIdentified.current = null;
  }, [user, business, isAuthenticated, isImpersonating, permissionRole]);

  return null;
}
